import { FormGroup } from '@angular/forms';
import type { FormField } from '../../interfaces/form.interface';
import { DropdownData } from '../../interfaces/input.interface';
import {
  ReactiveFormValueUtils,
  type ReactiveDropdownOption,
} from '../../model/reactive-form-value.utils';
import {
  ReactiveChangeOriginPolicy,
  type ReactiveChangeOrigin,
} from '../change-origin/reactive-change-origin';

type ReactiveDropdownAutoSelectConfig<T> = {
  readonly getModel: () => FormGroup | null;
  readonly applySelectedValue: (field: FormField<T>, value: unknown, origin: ReactiveChangeOrigin) => void;
};

export class ReactiveDropdownAutoSelectService<T> {

  public constructor(private readonly config: ReactiveDropdownAutoSelectConfig<T>) {}

  public autoSelect(field: FormField<T>, data: DropdownData<unknown>, changeOrigin: ReactiveChangeOrigin): boolean {
    const autoSelectMode = field.dependencyBehaviour?.autoSelect ?? field.input?.dropdownConfiguration?.autoSelect;

    if (autoSelectMode !== 'singleOption') {
      return false;
    }

    const fieldName = field.name ?? '';
    const model = this.config.getModel();
    const control = fieldName !== '' ? model?.get(fieldName) : null;

    if (!control) {
      return false;
    }

    if (!ReactiveChangeOriginPolicy.shouldAutoSelect(changeOrigin, ReactiveFormValueUtils.hasNonEmptyValue(control.value))) {
      return false;
    }

    const primaryKey = ReactiveFormValueUtils.getFieldControlValueKey(field);
    const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(field);
    const realOptions = ReactiveFormValueUtils.getDropdownOptions(data)
      .filter((option) => this.isRealSelectableOption(option));

    if (realOptions.length !== 1) {
      return false;
    }

    const [singleOption] = realOptions;

    if (this.doesControlValueMatchOption(control.value, singleOption?.value, primaryKey, identityKeys)) {
      return false;
    }

    const currentValueIsAvailable = this.isCurrentValueAvailableInOptions(control.value, realOptions, primaryKey, identityKeys);

    if (ReactiveFormValueUtils.hasNonEmptyValue(control.value)) {
      if (currentValueIsAvailable || field.dependencyBehaviour?.preserveValueWhenOptionsMissing === true) {
        return false;
      }
    }

    model?.patchValue({ [fieldName]: singleOption?.value }, { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
    this.config.applySelectedValue(field, singleOption?.value, 'autoSelect');

    return true;
  }

  public isCurrentValueAvailableInOptions(
    currentValue: unknown,
    options: ReadonlyArray<ReactiveDropdownOption>,
    primaryKey: string,
    identityKeys: readonly string[] = [],
  ): boolean {
    const currentId = ReactiveFormValueUtils.extractComparableId(currentValue, primaryKey, identityKeys);

    if (currentId === null) {
      return false;
    }

    return ReactiveFormValueUtils.createOptionsIndex(options, primaryKey, identityKeys).has(String(currentId));
  }

  private isRealSelectableOption(option: ReactiveDropdownOption): boolean {
    const label = String(option?.label ?? '').trim();

    return option?.value !== null && option?.value !== undefined && label !== '' && label !== '...' && option['disabled'] !== true;
  }

  private doesControlValueMatchOption(
    currentValue: unknown,
    optionValue: unknown,
    primaryKey: string,
    identityKeys: readonly string[] = [],
  ): boolean {
    const currentId = ReactiveFormValueUtils.extractComparableId(currentValue, primaryKey, identityKeys);
    const optionId = ReactiveFormValueUtils.extractComparableId(optionValue, primaryKey, identityKeys);

    return currentId !== null && optionId !== null && String(currentId) === String(optionId);
  }
}
